import { layout } from '@universityofmaryland/web-elements-library/composite';
import { createSlot } from '@universityofmaryland/web-utilities-library/elements';
import * as validation from '@universityofmaryland/web-utilities-library/validation';
import { Attributes, Model, Slots } from '@universityofmaryland/web-model-library';
import {
  CreateComponentFunction,
  ComponentRegistration,
  SlotConfiguration,
} from '../../_types';

const tagName = 'umd-layout-image-expand';

const slots: SlotConfiguration = {
  content: {
    required: true,
  },
  image: {
    required: true,
  },
};

const createComponent: CreateComponentFunction = (element) =>
  layout.image.expand({
    content: createSlot('content'),
    image: validation.getValidatedSlotImage({
      element,
      slotName: Slots.name.assets.image,
    }) as HTMLImageElement,
    includesAnimation: Attributes.includesFeature.animation({ element }),
  });

/**
 * Image Expand Layout
 *
 * A layout component that presents content alongside an expandable image. The image can extend
 * beyond the normal content boundaries to create visual impact. Ideal for showcasing featured
 * content, hero sections, or highlighting important information with striking visuals.
 *
 * ## Custom Element
 * `<umd-layout-image-expand>`
 *
 * ## Slots
 * - `content` - Main content area (required, accepts: any elements)
 * - `image` - Featured image that can expand (required, accepts: img)
 *
 * ## Attributes
 * - `data-animation` - Enable scroll-triggered expand animation:
 *   - `false` - Disables the animation and shows the completed/expanded state
 *   - Default - Enabled (`true`)
 *
 * @example
 * ```html
 * <!-- Basic image expand layout -->
 * <umd-layout-image-expand>
 *   <div slot="content">
 *     <h2>Featured Research</h2>
 *     <p>Breakthrough discoveries in quantum computing...</p>
 *   </div>
 *   <img slot="image" src="research-lab.jpg" alt="Quantum Lab" />
 * </umd-layout-image-expand>
 * ```
 *
 * @example
 * ```html
 * <!-- With rich content -->
 * <umd-layout-image-expand>
 *   <article slot="content">
 *     <h1>Campus Innovation Hub</h1>
 *     <p>The new Innovation Hub brings together students, faculty, and industry partners...</p>
 *     <a href="/innovation">Learn More</a>
 *   </article>
 *   <img slot="image" src="innovation-hub.jpg" alt="Innovation Hub Interior" />
 * </umd-layout-image-expand>
 * ```
 *
 * @category Components
 * @since 1.0.0
 */
export const LayoutImageExpand: ComponentRegistration = Model.defineComponent({
  tagName,
  slots,
  createComponent,
}, { eager: false });

/** Backwards compatibility alias for grouped exports */
export { LayoutImageExpand as imageExpand };
